import { Nullable } from '@orcar/common';
import {
  CalendarProps,
  CalendarWeek,
  CalendarWeekTitle,
  DAYS_OF_WEEK,
  Icon,
  LeftIcon,
  RightIcon,
  useDetectMobile,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const WebCalendar = ({
  year,
  month,
  pickUpDate,
  dropOffDate,
  todayReservationDeadline,
  onSelectPickup,
  onSelectDropOff,
  onClickMoveCalendar,
}: CalendarProps) => {
  const today = dayjs();
  const calendarMonth = dayjs()
    .year(year)
    .month(month - 1)
    .date(1)
    .hour(0)
    .minute(0);

  const [weeks, setWeeks] = useState<Nullable<Dayjs>[][]>([]);

  const { isMobile } = useDetectMobile();

  useEffect(() => {
    const monthDates: Nullable<Dayjs>[] = [];
    for (let padStart = 0; padStart < calendarMonth.day(); padStart++)
      monthDates.push(null);
    for (let date = 1; date <= calendarMonth.daysInMonth(); date++)
      monthDates.push(calendarMonth.set('date', date));
    const padEndCount = DAYS_OF_WEEK - (monthDates.length % DAYS_OF_WEEK);
    if (padEndCount < DAYS_OF_WEEK)
      for (let padEnd = 0; padEnd < padEndCount; padEnd++)
        monthDates.push(null);

    const newWeeks: Nullable<Dayjs>[][] = [];
    for (let i = 0; i < monthDates.length / DAYS_OF_WEEK; i++)
      newWeeks.push(monthDates.slice(i * DAYS_OF_WEEK, (i + 1) * DAYS_OF_WEEK));

    setWeeks(newWeeks);
  }, [year, month]);

  return (
    <Wrapper>
      <CalendarTitle>
        {`${year}년 ${month}월`}
        <PaginationButtonWrapper>
          {year == today.year() ? (
            month > today.month() + 1 && (
              <Icon
                src={LeftIcon}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  if (onClickMoveCalendar) onClickMoveCalendar('previous');
                }}
                width={6}
                height={10}
                wrapperWidth='25px'
                wrapperHeight='25px'
              />
            )
          ) : (
            <Icon
              src={LeftIcon}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                if (onClickMoveCalendar) onClickMoveCalendar('previous');
              }}
              width={6}
              height={10}
              wrapperWidth='25px'
              wrapperHeight='25px'
            />
          )}
          <Icon
            src={RightIcon}
            onClick={() => {
              if (onClickMoveCalendar) onClickMoveCalendar('next');
            }}
            width={6}
            height={10}
            wrapperWidth='25px'
            wrapperHeight='25px'
          />
        </PaginationButtonWrapper>
      </CalendarTitle>
      <MonthWrapper>
        <CalendarWeekTitle size='small' />
        {weeks.map((week, idx) => (
          <CalendarWeek
            elements={week}
            today={today}
            key={idx}
            size='small'
            dateInfo={{
              pickUpDate,
              dropOffDate,
              onSelectPickup,
              onSelectDropOff,
            }}
            isMobile={isMobile}
            todayReservationDeadline={todayReservationDeadline}
          />
        ))}
      </MonthWrapper>
    </Wrapper>
  );
};

export { WebCalendar };

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
`;

const CalendarTitle = styled.span`
  ${({ theme }) => theme.FONT.B1}
  display: flex;
  justify-content: space-between;

  margin-bottom: 8px;
  width: 100%;
  line-height: 19px;
  text-align: center;
  font-weight: 600;
`;

const PaginationButtonWrapper = styled.span`
  display: flex;
  gap: 0 5px;
`;

const ArrowIcon = styled('img')`
  width: 6px;
  height: 10px;
  cursor: pointer;
`;

const InfoText = styled.p`
  width: 100%;
  height: 16px;
  margin-bottom: 15px;
  ${({ theme }) => theme.FONT.B2};
  line-height: 16px;
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;

const MonthWrapper = styled.div`
  width: 100%;
`;
